import fetch from 'isomorphic-unfetch';
import { ImpacterProfile } from '../types/AuthTypes';
import { ImpacterResponse } from '../types/ImpacterTypes';
import { Provider } from '../types/PaymentTypes';
import {
  Payout,
  PayoutResponse,
  ProjectTransactionResponse,
  TransactionAttachment,
} from '../types/ProjectTypes';
import { CurrentUserResponse } from '../types/UserTypes';
import { getDeviceId } from './analytics';
import config from './config';
import { getHash } from './crypto';
import { ApiError } from './error';
import { getHost } from './helpers/host';
import { getToken, removeToken } from './token';

const API_URL = config.API_URL;

const handleErrorCategory = async (errorType: string, response: Response) => {
  let message = '';
  let json: Record<string, unknown> = {};
  try {
    message = await response.text();
    json = JSON.parse(message);
  } catch {
    message = message || response.statusText;
  }

  const error = new ApiError(
    errorType,
    response.status,
    json.errorId as string,
    json.errorCode as string,
    (json.error || json.errorMessage) as string,
    message,
  );
  throw error;
};

const handleMiscErrors = async (response: Response) => {
  if (response.status >= 400 && response.status < 500) {
    await handleErrorCategory('Client error', response);
  } else if (response.status >= 500 && response.status < 600) {
    await handleErrorCategory('Server error', response);
  }
};

export type queryParams =
  | string
  | number
  | {
      [x: string]: string | string[] | number | undefined;
    };

type requestOptions = {
  query?: queryParams;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: {
    [x: string]: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any;
  fullUrl?: boolean;
};

export const authenticatedFetch = async <T>(
  path: string,
  options: requestOptions = {},
): Promise<T> => {
  const url = options.fullUrl ? path : `${API_URL}${path}${toQueryString(options.query)}`;

  const response = await fetch(url, {
    method: options.method || 'GET',
    headers: new Headers({
      Authorization: `Bearer ${getToken()}`,
      ...options.headers,
    }),
    body: options.body,
  });

  if (response.status === 401) {
    removeToken();
    await handleErrorCategory('Unauthorized error', response);
  }

  await handleMiscErrors(response);

  const contentType = response.headers.get('content-type');
  if (contentType?.match('application/json')) {
    const json = await response.json();
    return json as T;
  }

  return response as unknown as T;
};

export async function getJWTToken(accessToken: string) {
  const response = await fetch(`${API_URL}authentication/v2/firebase?access_token=${accessToken}`);

  if (response.status === 401) {
    const body = await response.json();
    if (body?.code == 103) {
      throw new Error('missing_email');
    }

    if (body?.code == 106) {
      throw new Error(body.message);
    }

    throw new Error();
  }

  await handleMiscErrors(response);

  return await response.json();
}

// User
export const getCurrentUser = async () =>
  await authenticatedFetch<CurrentUserResponse>('users/v2/me');

export const getCurrentImpacterProfile = async () =>
  await authenticatedFetch<ImpacterProfile>('content/v2/impacters/me');

export const acceptTermsOfUse = async (slug: string) =>
  await authenticatedFetch(`users/v2/me/accept-terms/${slug}`, { method: 'POST' });

export const setReferredBy = async (userId: string) =>
  await authenticatedFetch(`users/v2/me/referredBy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ referredBy: userId }),
  });

// FIXME: update to v2 after the v2 endpoint has been implemented
export const addEmailToNewsLetter = async (email: string) => {
  const response = await fetch(`${API_URL}api/users/email-list`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (response.status === 409) throw new Error('Email is already registered');
  else if (response.status != 204) throw new Error('Got error: ' + response.status);
};

export const sendEmailVerification = async (firebaseToken?: string, url?: string) => {
  const response = await fetch(`${API_URL}authentication/v2/firebase/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accessToken: firebaseToken, url }),
  });
  if (response.status !== 200) throw new Error('Failed email verification');
};

export type Currency = {
  code: string;
  name: string;
  cents: number;
  providers: string[];
};
export const getCurrencies = () =>
  getFromGalaxyCDN<{ result: Currency[] }>(`accounting/v2/currencies`);

export type PaymentProvider = {
  name: string;
  configuration: {
    publishableKey: string;
    buttonKey?: string;
    environment?: string;
    region?: string;
  };
};
export const getPaymentProviders = (currencyCode: string) =>
  getFromGalaxyCDN<{
    result: PaymentProvider[];
  }>(`accounting/v2/providers?currencyCode=${currencyCode}`);

export type AmountSuggestion = {
  amount: number;
  default: boolean;
  currency: { code: string; name: string; symbol: string };
};

export const getAmountSuggestions = ({
  currencyCode,
  configuration = 'default',
}: {
  currencyCode: string;
  configuration?: string;
}) =>
  getFromGalaxyCDN<{ result: AmountSuggestion[] }>(
    `accounting/v2/amount-suggestions/${currencyCode}/${configuration}`,
  );

export type PaymentMethod = {
  id: string;
  default?: boolean;
  created: string;
  card: {
    year: number;
    brand: string;
    last4: string;
    month: number;
  };
};
export type PaymentMethodsResponse = {
  publicKey: string;
  result: PaymentMethod[];
};

export const getStripePaymentMethods = async () =>
  await authenticatedFetch<PaymentMethodsResponse>(
    `accounting/v2/providers/stripe/payment-methods`,
  );

export const deleteStripePaymentMethod = async (paymentMethodId: string) =>
  await authenticatedFetch(`accounting/v2/providers/stripe/payment-methods/${paymentMethodId}`, {
    method: 'DELETE',
  });

export const setDefaultStripePaymentMethod = async (paymentMethodId: string) =>
  await authenticatedFetch(`accounting/v2/providers/stripe/payment-methods/${paymentMethodId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      default: true,
    }),
  });

export const addStripePaymentMethod = async (paymentMethodId: string) =>
  authenticatedFetch<{ pendingSetupIntent: { status: string; client_secret: string } | null }>(
    `accounting/v2/providers/stripe/payment-methods`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        id: paymentMethodId,
      }),
    },
  );

export const getGiftAmount = async (currencyCode: string) =>
  await authenticatedFetch<{ amount: number }>(
    `accounting/v2/gifts/amount-suggestion?currencyCode=${currencyCode}`,
  );

type BuyGiftParams = {
  currencyCode: string;
  paymentMethodId: string;
  user?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  period: number;
  receiverName: string;
  message?: string;
};

type BuyGiftResponse = {
  id: string;
  giverUserId: number;
  paymentRequest: {
    id: string;
    reference: string;
    publicKey: string;
    requiresAction: boolean;
    session: {
      clientSecret: string;
      status: string;
    };
  };
};

export const buyGift = async ({
  currencyCode,
  paymentMethodId,
  user,
  period,
  receiverName,
  message,
}: BuyGiftParams) =>
  await authenticatedFetch<BuyGiftResponse>(`accounting/v2/gifts`, {
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({
      currencyCode,
      period,
      receiverName,
      message,
      payment: {
        provider: 'stripe',
        paymentMethodId,
      },
      giver: user,
    }),
  });

export enum GiftStatus {
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  PAID = 'PAID',
  REDEEMED = 'REDEEMED',
  EXPIRED = 'EXPIRED',
}

type RedeemPayloadResponse = {
  id: string;
  name: string;
  message: string;
  period: number;
  amount: number;
  currencyCode: string;
  lastRedeemDate: string;
  status: GiftStatus;
};

export const decryptRedeemPayload = async (payload?: string | undefined) =>
  await getFromGalaxy<RedeemPayloadResponse>(`accounting/v2/gifts/redeem?payload=${payload}`);

type RedeemGiftParams = {
  payload: string;
  tagIds?: string[] | null;
  user?: { email?: string; firstName?: string; lastName?: string };
};

export const redeemGift = ({ payload, tagIds, user }: RedeemGiftParams) =>
  authenticatedFetch<{ userId: number }>('accounting/v2/gifts/redeem', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      payload,
      tagIds,
      user,
    }),
  });

export type UserPortfolio = {
  id: string;
  userId: number;
  currentVersion: number;
  items?: { id: number; impacterId: number }[];
};

export const getUserPortfolio = async () =>
  await authenticatedFetch<{
    result: UserPortfolio[];
  }>('accounting/v2/user-portfolios?me=true');

export type UserSubscription = {
  id: number;
  userId: number;
  currencyCents: number;
  providerReference: string;
  providerData?: {
    partnerId: string;
    donationProgramId: string;
    giftId: string;
  };
  endDate: string | null;
  createdAt: string | null;
  paymentProviderName: string;
  status: string;
  amount?: number;
  interval?: string;
  intervalCount?: number;
  currencyCode: string;
  currencyName: string;
  currency: {
    name: string;
    code: string;
    cents: number;
    symbol: string;
  };
  items: {
    id: number;
    impacterIds: number[] | null;
    totalAmount: number;
    type: string;
  }[];
};

type UserSubscriptionResponse = {
  result: UserSubscription[];
  impacterCount: number;
};

export const getUserSubscriptions = async () =>
  await authenticatedFetch<UserSubscriptionResponse>('accounting/v2/subscriptions?me=true');

export type SubscriptionImpacter = {
  id: number;
  added: boolean;
  deleted: boolean;
  previousPartOfAmount: number;
  currentPartOfAmount: number;
};

export type PreviewSubscriptionResponse = {
  validationErrors?: {
    errorCode: string;
    errorMessage: string;
  }[];
  subscriptionDetails?: {
    currentTotalAmount: number;
    previousTotalAmount: number;
    currencyCode: string;
    currencyCents: number;
    donationProgramId?: string;
    items: {
      id?: number;
      type: string;
      previousAmount: number;
      currentAmount: number;
      impacters: SubscriptionImpacter[];
    }[];
  };
  invoicePreview?: {
    created: string;
  };
};

type PreviewSubscriptionParameters = {
  currencyCode: string;
  paymentProvider: Provider;
  includeProviderData?: boolean;
  items: { amount: number }[];
};

// DEPRECATED IN GALAXY
export const previewSubscription = async ({
  currencyCode,
  paymentProvider,
  includeProviderData,
  items,
}: PreviewSubscriptionParameters) =>
  await authenticatedFetch<PreviewSubscriptionResponse>(`accounting/v2/subscriptions/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      currencyCode,
      paymentProvider,
      includeProviderData,
      items,
    }),
  });

type CreateSubscriptionParams = {
  currencyCode: string;
  items: { amount: number }[];
  paymentMethodId: string;
  user?: { firebaseToken?: string } | { email?: string; firstName?: string; lastName?: string };
  affiliates?: {
    affiliateName?: string;
    affiliateId?: string;
    publishable?: boolean;
    message?: string;
  }[];
};

type CreateSubscriptionResponse = {
  subscription: { id: number; currentVersion: number; externalId: string };
  providerResponse: {
    providerReference: string;
    items: { id: string }[];
    pendingSetupIntent: { status: string; client_secret: string } | null;
  } | null;
  user: { id: string };
};

export const createSubscription = async ({
  currencyCode,
  items, // DEPRECATED
  paymentMethodId,
  user,
  affiliates,
}: CreateSubscriptionParams) =>
  authenticatedFetch<CreateSubscriptionResponse>(`accounting/v2/subscriptions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      currencyCode,
      paymentProvider: 'stripe',
      items,
      affiliates,
      paymentMethodId,
      user,
    }),
  });

type CreateStripeSubscriptionParams = {
  affiliateName?: string;
  currencyCode: string;
  amount: number;
  externalImpacterIds?: string[];
  categoryIds?: string[];
  paymentMethodId: string;
  user?: { email?: string; firstName?: string; lastName?: string; emailOptIn?: boolean };
};

type CreateStripeSubscriptionResponse = {
  subscription: { id: number; currentVersion: number; externalId: string };
  providerResponse: {
    providerReference: string;
    items: { id: string }[];
    pendingSetupIntent: { status: string; client_secret: string } | null;
  } | null;
  user: { id: string };
};

export const createStripeSubscription = async ({
  affiliateName,
  amount,
  categoryIds,
  currencyCode,
  externalImpacterIds,
  paymentMethodId,
  user,
}: CreateStripeSubscriptionParams) => {
  return authenticatedFetch<CreateStripeSubscriptionResponse>(
    `accounting/v2/providers/stripe/subscriptions`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        affiliateName,
        currencyCode,
        amount,
        externalImpacterIds,
        categoryIds,
        paymentMethodId,
        user,
      }),
    },
  );
};

type SubscriptionReceiptResponse = {
  id: string;
  status: string;
  currencyCode: string;
  amount: number;
  createdAt: string;
  firstName: string;
  lastName: string;
  email: string;
  description: string;
};

export const getSubscriptionReceipt = async (receipt?: string | number) =>
  await authenticatedFetch<SubscriptionReceiptResponse>(
    `accounting/v2/subscriptions/${receipt}/receipt`,
  );

export type AffiliateSupporter = {
  firstName: string;
  lastName: string;
  message: string;
  externalProfileImage: string;
  currencyCode: string;
  amount: number;
};

type AffiliateSupportersResponse = {
  supporters: AffiliateSupporter[];
  fetchMoreUrl: string;
};

export const getAffiliateSupporters = async (
  affiliateName: string,
  orderBy: 'createdAt' | 'amount',
): Promise<AffiliateSupportersResponse> => {
  const {
    result: supporters,
    pagination: { prev: fetchMoreUrl },
  } = await getFromGalaxyCDN<{ result: AffiliateSupporter[]; pagination: { prev: string } }>(
    `accounting/v2/affiliates/${affiliateName}/users?paginationType=first&limit=6&orderBy=${orderBy}`,
  );

  return { supporters, fetchMoreUrl };
};

export type AffiliateStatisticsResponse = {
  monthlyAmount: number;
  monthlyAmountCurrency: {
    code: string;
    name: string;
    symbol: string;
  };
  subscriberCount: number;
};

export const getAffiliateStatistics = async (
  affiliateName: string,
  currencyCode: string,
): Promise<AffiliateStatisticsResponse> =>
  await authenticatedFetch<AffiliateStatisticsResponse>(
    `accounting/v2/affiliates/${affiliateName}/statistics?currencyCode=${currencyCode}`,
  );

/**
 * @param projectId - projectId as number
 *   Fetch project balances per project
 */
export const getProjectBalances = (projectId: string | number) =>
  authenticatedFetch(`accounting/v2/balances/projects/${projectId}?includeExtendedBalances=true`);

/**
 * @param projectId - projectId as number
 *   Fetch transactions per project.
 */
export const getProjectTransactions = (
  projectId: number,
  options: {
    query?: queryParams;
  },
) =>
  authenticatedFetch<ProjectTransactionResponse>(
    `accounting/v2/projects/${projectId}/transactions`,
    options,
  );

/**
 * @param projectId - projectId as number
 *   Fetch payouts per project.
 */
export const getPayouts = (projectId: number) =>
  authenticatedFetch<PayoutResponse>(`accounting/v2/projects/${projectId}/payouts`);

/**
 * @param projectId - projectId as number
 * @param payoutItemId - payoutItemId as string
 *   Fetch payout item
 */
export const getPayoutItem = (projectId: number, payoutItemId: string) =>
  authenticatedFetch<Payout>(`accounting/v2/projects/${projectId}/payout-items/${payoutItemId}`);

/**
 * @param projectId - projectId as number
 * @param transactionId - transactionId as number
 *
 * Fetch attachments for a transactions
 */
export const getTransactionAttachments = (transactionId: number) =>
  authenticatedFetch<{
    attachments: TransactionAttachment[];
  }>(`accounting/v2/transactions/${transactionId}/attachments`);

/**
 * @param query - impacterId or query object
 *   e.g. { impacterNickname: 'Bob }. See galaxy GET /impacters route signature for details
 */
export const getImpacter = async (query: queryParams): Promise<ImpacterResponse | null> => {
  if (typeof query == 'number') {
    try {
      const response = await getFromGalaxy<ImpacterResponse>(`content/v2/impacters/${query}`);
      return response;
    } catch (error) {
      // @ts-expect-error FIXME: handle error types explicitly for improved error handling
      if (error?.status == 404) return null;
      throw error;
    }
  } else {
    const response = await getFromGalaxy<{ result: ImpacterResponse[] }>(
      'content/v2/impacters',
      query,
    );
    return response.result[0];
  }
};

type GetImpacters = {
  impacterIds?: string | string[];
  anyTagNames?: string | string[];
  fields?: string | string[];
};
export const getImpacters = async ({ impacterIds, anyTagNames, fields }: GetImpacters = {}) =>
  await getFromGalaxyCDN<{ result: ImpacterResponse[] }>('content/v2/impacters', {
    impacterId: impacterIds,
    anyTagName: anyTagNames,
    field: fields,
  });

export type CategoryResponse = {
  id: string;
  name: string;
  impacterIds?: number[];
};

export type CategoriesResponse = {
  result: CategoryResponse[];
};

export const getCategory = async (id: string) =>
  await getFromGalaxyCDN<CategoryResponse>(`content/v2/categories/${id}`);

export const getCategories = async ({ impacterId }: { impacterId?: number } = {}) =>
  await getFromGalaxyCDN<CategoriesResponse>(`content/v2/categories`, { impacterid: impacterId });

export const getImpactersWithStatus = async ({
  impacterIds,
  anyTagNames,
  fields,
  status,
}: GetImpacters & {
  status: 'ACTIVE' | 'PREVIEW';
}) =>
  await getFromGalaxy<{ result: ImpacterResponse[] }>('content/v2/impacters', {
    impacterId: impacterIds,
    anyTagName: anyTagNames,
    field: fields,
    status,
  });

export async function getImpacterProjects(impacterId: string) {
  const { result } = await authenticatedFetch<{ result: unknown }>(
    `accounting/v2/projects?impacterId=${impacterId}`,
  );
  return result;
}

export const getLocale = async () =>
  await getFromGalaxy<{
    suggestion: {
      countryCode: string;
      timezone?: string;
      city?: string;
      latLong?: string;
      defaultCurrencyCode: string;
    };
  }>(`api/locale`);

type PaymentRequestResponse = {
  id: string;
  status: string;
  currencyCode: string;
  amount: number;
  createdAt: string;
  firstName: string;
  lastName: string;
  email: string;
  description: string;
};

export const getPaymentRequestByExternalId = async (externalId: string) =>
  await authenticatedFetch<PaymentRequestResponse>(`accounting/v2/payment-requests/${externalId}`);

type PaymentRequestParamsStripe = {
  amount: number;
  currencyCode: string;
  projectIds?: number[];
  impacterIds?: number[];
  paymentMethod: string;
  email: string;
  firstName?: string;
  lastName?: string;
  emailOptIn: boolean;
  initiator?: string;
  categoryIds?: string[];
  language?: string;
  skipAmountValidation?: boolean;
};

type PaymentRequestResponseStripe = {
  id: string;
  requiresAction: boolean;
  paymentIntentClientSecret: string;
  status: string;
};

export const createPaymentRequestForStripe = async ({
  amount,
  currencyCode,
  impacterIds,
  projectIds,
  paymentMethod,
  email,
  firstName,
  lastName,
  initiator,
  emailOptIn,
  categoryIds,
  language,
  skipAmountValidation,
}: PaymentRequestParamsStripe) =>
  await authenticatedFetch<PaymentRequestResponseStripe>(
    'accounting/v2/providers/stripe/payment-requests',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Request-Check': await getHash(email),
        ...(skipAmountValidation ? { 'Skip-Amount-Validation': 'true' } : {}),
      },
      body: JSON.stringify({
        amount,
        currencyCode,
        projectIds,
        impacterIds,
        paymentMethod,
        email,
        firstName,
        lastName,
        emailOptIn,
        initiator,
        categoryIds,
        language,
      }),
    },
  );

type PaymentRequestParamsKlarna = {
  amount: number;
  currencyCode: string;
  language: string;
  country: string;
  initiator: string;
  impacterIds: number[];
  categoryIds?: string[];
  skipAmountValidation?: boolean;
};

export const createPaymentIntentForKlarna = async ({
  amount,
  currencyCode,
  language,
  country,
  initiator,
  impacterIds,
  categoryIds,
  skipAmountValidation,
}: PaymentRequestParamsKlarna) =>
  await authenticatedFetch<{ id: string }>('accounting/v2/providers/klarna/payment-requests', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(skipAmountValidation ? { 'Skip-Amount-Validation': 'true' } : {}),
    },
    body: JSON.stringify({
      amount,
      currencyCode,
      language,
      country,
      initiator,
      impacterIds,
      categoryIds,
    }),
  });

export const createKlarnaOrder = async (
  authorization_token: string,
  paymentRequestId: string,
  amount: number,
  currencyCode: string,
) =>
  await authenticatedFetch('accounting/v2/providers/klarna/order', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      authorization_token,
      paymentRequestId,
      amount,
      currencyCode,
    }),
  });

export const getFromEnterpriseBackend = async <T>(
  path: string,
  query?: queryParams,
): Promise<T> => {
  return await get(`${getHost()}/api/${path}`, { query, name: 'Enterprise' });
};

// TODO: this method is very close to authenticatedFetch.
// Their names should be put inline considering the fact that getFromEnterpriseBackend also exist
export const getFromGalaxy = async <T>(path: string, query?: queryParams): Promise<T> => {
  return await get<T>(`${config.API_URL}${path}`, { query, name: 'Galaxy' });
};

export const getFromGalaxyCDN = async <T>(path: string, query?: queryParams): Promise<T> => {
  return await get<T>(`${config.CDN_API_URL}${path}`, { query, name: 'Galaxy CDN' });
};

export const get = async <T>(
  url: string,
  { query, name }: { query?: queryParams; name?: string } = {},
): Promise<T> => {
  const response = await fetch(url + toQueryString(query));
  if (!response.ok) await handleErrorCategory(name ? `Api ${name}` : 'Api', response);
  return await response.json();
};

function toQueryString(query?: queryParams): string {
  if (!query) return '';

  if (typeof query == 'object') {
    // { a: 1, b: [2, 3, undefined], c: undefined } to a=1&b=2&b=3
    query = Object.entries(query)
      .flatMap(([key, values]) =>
        []
          // @ts-expect-error ts-migrate(2769) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
          .concat(values)
          .filter((value) => value !== undefined)
          .map((value) => [encodeURIComponent(key), encodeURIComponent(value)]),
      )
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
  }

  return `?${query}`;
}

export function claimUserInvite(payload: string, firebaseId: string) {
  return authenticatedFetch(`/authentication/v2/users/invite`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      payload,
      firebaseId,
    }),
  });
}

export const postAirtablePollChoice = async ({
  pollId,
  title,
}: {
  pollId: string;
  title: string;
}) => {
  await fetch(`${getHost()}/api/poll/airtable`, {
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      pollId,
      title,
      deviceId: getDeviceId(),
    }),
  });
};
